import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Configure axios to send cookies
axios.defaults.withCredentials = true;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const notifIntervalRef = useRef(null);

  useEffect(() => {
    checkAuth();
  }, []);

  // Request browser notification permission once user is set
  useEffect(() => {
    if (user && "Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, [user]);

  // Poll for pending popup notifications every 60 seconds while logged in
  const pollPopupNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications/pending`);
      const notifications = res.data?.notifications || [];
      if (notifications.length === 0) return;

      // Show a browser notification for each pending popup
      const canShow = "Notification" in window && Notification.permission === "granted";
      notifications.forEach((n) => {
        if (canShow) {
          new Notification(n.title || "Task Reminder", {
            body: n.body || "",
            icon: "/logo192.png",
          });
        }
      });

      // Mark all as read
      await axios.delete(`${API}/notifications/pending`);
    } catch (err) {
      // Silently ignore — user might not be authenticated
    }
  }, []);

  useEffect(() => {
    if (user) {
      // Fire once immediately, then every 60 seconds
      pollPopupNotifications();
      notifIntervalRef.current = setInterval(pollPopupNotifications, 60_000);
    } else {
      clearInterval(notifIntervalRef.current);
      notifIntervalRef.current = null;
    }
    return () => clearInterval(notifIntervalRef.current);
  }, [user, pollPopupNotifications]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, rememberMe = false) => {
    const response = await axios.post(`${API}/auth/login`, { email, password, remember_me: rememberMe });
    setUser(response.data.user);
    checkAuth(); // fetch full user data (team_names, etc.) in background
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
  };

  const forgotPassword = async (email) => {
    const response = await axios.post(`${API}/auth/forgot-password`, { email });
    return response.data;
  };

  const resetPassword = async (token, newPassword) => {
    const response = await axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword });
    return response.data;
  };

  const value = {
    user,
    loading,
    login,
    logout,
    forgotPassword,
    resetPassword,
    checkAuth,
    isAdmin: user?.role === "ADMIN",
    isTeamLeader: user?.role === "TEAM_LEADER",
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
